import { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { trackGameEvent } from '../utils/gameEvents';

// A subtle chocolate emoji tucked in a corner. Click 5 times within 30s to
// "discover" it. Server validates the code and grants the secret_hunter sticker.
// Once claimed, the chocolate disappears for that user (no farming).
const CODE = 'dashboard_chocolate';
const CLICKS_NEEDED = 5;
const WINDOW_MS = 30_000;

export default function HiddenChocolate() {
  const { user } = useAuth();
  const [discovered, setDiscovered] = useState(false);
  const [claimed, setClaimed] = useState(true);   // start hidden until /me check
  const clicksRef = useRef([]);

  // On mount, check whether this user has already claimed this discovery —
  // hide the chocolate if so.
  useEffect(() => {
    if (!user || !['admin', 'manager', 'agent'].includes(user.role)) return;
    let cancelled = false;
    api.get('/discoveries/me').then((r) => {
      if (cancelled) return;
      const list = r.data?.claimed || [];
      setClaimed(list.includes(CODE));
    }).catch(() => {
      // If the endpoint fails, default to NOT showing — better safe.
      setClaimed(true);
    });
    return () => { cancelled = true; };
  }, [user]);

  if (!user || !['admin', 'manager', 'agent'].includes(user.role)) return null;
  if (claimed && !discovered) return null;

  const handleClick = async () => {
    if (discovered) return;
    const now = Date.now();
    clicksRef.current = [...clicksRef.current, now].filter((t) => now - t < WINDOW_MS);
    trackGameEvent('chocolate_click', { count: clicksRef.current.length });
    if (clicksRef.current.length >= CLICKS_NEEDED) {
      setDiscovered(true);
      try {
        await api.post('/discoveries/claim', { code: CODE });
        // Sticker grant socket fires its own toast — nothing else needed here.
      } catch {
        // silent — user just sees no toast
      }
    }
  };

  return (
    <button
      type="button"
      aria-hidden="true"
      tabIndex={-1}
      onClick={handleClick}
      style={{
        position: 'fixed',
        right: 6,
        top: 6,
        background: 'transparent',
        border: 'none',
        outline: 'none',
        padding: 4,
        cursor: 'default',          // intentionally not 'pointer' — the magic is in finding it
        opacity: discovered ? 1 : 0.18,
        fontSize: 18,
        lineHeight: 1,
        zIndex: 5,
        userSelect: 'none',
        transition: 'opacity 200ms ease, transform 200ms ease',
        transform: discovered ? 'scale(1.6) rotate(-12deg)' : 'scale(1)',
      }}
    >
      🍫
    </button>
  );
}
